import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { 
  Button, 
  Input, 
  Card, 
  Typography, 
  Space, 
  Modal, 
  Form, 
  InputNumber, 
  Tooltip, 
  Alert,
  Divider,
  Select,
  Table,
  Tag,
  Tabs,
  Radio,
  Row,
  Col,
  Switch
} from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  EditOutlined, 
  ClearOutlined, 
  InfoCircleOutlined,
  FontSizeOutlined,
  CopyOutlined,
  PrinterOutlined,
  SettingOutlined,
  CodeOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import printTemplate from '../../utils/printExpTemplate'; 
import { numberToFrenchWords } from '../../utils/numberToFrenchWords';


const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

// Template placeholder syntax
const TEMPLATE_START = '{{';
const TEMPLATE_END = '}}';
// Condition syntax: {{if:variable=value:result}}
const CONDITION_SYNTAX = '{{if:';
const CONDITION_SEPARATOR = ':';

// Available variables from JournalExp model
const JOURNAL_EXP_VARIABLES = [
  { name: 'id',  type: 'Number' },
  { name: 'date_fac', type: 'Date' },
  { name: 'ref_fac',  type: 'Text' },
  { name: 'com', type: 'Number' },
  { name: 'ent', type: 'Text' },
  { name: 'COMPTE', type: 'Text' },
  { name: 'CLIENT',  type: 'Text' },
  { name: 'VILLE', type: 'Text' },
  { name: 'pays', type: 'Text' },
  { name: 'MNT_TTC',  type: 'Number' },
  { name: 'm_pai',  type: 'Text' },
  { name: 'm_liv',  type: 'Text' },
  { name: 'VALIDER',  type: 'Boolean' },
  { name: 'date_exp',  type: 'Date' },
  { name: 'ref_exp',  type: 'Text' },
  { name: 'nbr_colis',  type: 'Number' },
  { name: 'lieu_liv', type: 'Text' },
  { name: 'nbre_recu', type: 'Number' },
  { name: 'port_p', type: 'Text' },
  { name: 'obser', type: 'Text' },
  { name: 'mt_remb', type: 'Number' },
  { name: 'reg_remb',  type: 'Boolean' },
  { name: 'date_reg',  type: 'Date' },
  { name: 'paie_fr_trans',  type: 'Boolean' },
  { name: 'ville_dest',  type: 'Text' },
  { name: 'date_recep',  type: 'Date' },
  { name: 'mt_fr_trans',  type: 'Number' },
  { name: 'nat_fr_trans',  type: 'Text' },
  { name: 'date_dev',  type: 'Date' },
  { name: 'ref_dev',  type: 'Text' },
  { name: 'der_maj',  type: 'Date' },
  { name: 'modeliv_dv',  type: 'Text' },
  { name: 'modep_dv',  type: 'Text' },
  { name: 'hv_dv', type: 'Text' },
  { name: 'N_depart',  type: 'Text' },
  { name: 'mnt_ttc_b', type: 'Number' },
  { name: 'Nat_mse',  type: 'Text' },
  { name: 'remarque',type: 'Text' },
  { name: 'mnt_ttc_dv',  type: 'Number' },
  { name: 'art_x',  type: 'Text' },
  { name: 'ADRESSE',  type: 'Text' },
  { name: 'TEL',  type: 'Text' },
];

// Expanded common conditions with more useful examples
const COMMON_CONDITIONS = [
  { variable: 'port_p', condition: 'PP', result: 'X', description: 'Show X if Port Payé' },
  { variable: 'port_p', condition: 'PD', result: 'X', description: 'Show X if Port Dû' },
  { variable: 'VALIDER', condition: 'true', result: '✓', description: 'Show checkmark if validated' },
  { variable: 'reg_remb', condition: 'true', result: 'REMBOURSÉ', description: 'Show if reimbursed' },
  { variable: 'paie_fr_trans', condition: 'true', result: 'PAYÉ', description: 'Show if transport fees paid' },
  { variable: 'm_pai', condition: 'CASH', result: 'Cash Payment', description: 'Show if payment is cash' },
  { variable: 'm_pai', condition: 'CHQ', result: 'Chèque', description: 'Show if payment is check' },
  { variable: 'm_pai', condition: 'VIR', result: 'Virement', description: 'Show if payment is wire transfer' },
  { variable: 'nbr_colis', condition: '1', result: 'Single Package', description: 'Show if single package' },
  { variable: 'nbr_colis', condition: '>1', result: 'Multiple Packages', description: 'Show if multiple packages' },
  // Add null check conditions
  { variable: 'CLIENT', condition: 'null', result: 'No Client', description: 'Show text if CLIENT is null/empty' },
  { variable: 'CLIENT', condition: '!null', result: 'Has Client', description: 'Show text if CLIENT is not null/empty' },
  { variable: 'mt_remb', condition: 'null', result: 'No Reimbursement', description: 'Show if reimbursement amount is null' },
  { variable: 'com', condition: '!null', result: 'Has Commission', description: 'Show if commission is not null' }
];

// Auto-condition field mappings for common text fields
const AUTO_CONDITION_FIELDS = [
  { 
    labelPattern: /port pay[ée]/i,
    variable: 'port_p',
    condition: 'PP',
    result: 'X'
  },
  { 
    labelPattern: /port d[ûu]/i,
    variable: 'port_p',
    condition: 'PD',
    result: 'X'
  },
  { 
    labelPattern: /valid[ée]/i,
    variable: 'VALIDER',
    condition: 'true',
    result: '✓'
  },
  { 
    labelPattern: /rembours[ée]/i,
    variable: 'reg_remb',
    condition: 'true',
    result: 'REMBOURSÉ'
  },
  { 
    labelPattern: /cash|espèces/i,
    variable: 'm_pai',
    condition: 'CASH',
    result: 'X'
  },
  { 
    labelPattern: /ch[èe]que/i,
    variable: 'm_pai',
    condition: 'CHQ',
    result: 'X'
  },
  { 
    labelPattern: /virement/i,
    variable: 'm_pai',
    condition: 'VIR',
    result: 'X'
  },
  // Add the extended null check auto-conditions
  { 
    labelPattern: /client required|client needed/i,
    variable: 'CLIENT',
    condition: '!null',
    result: '✓'
  },
  { 
    labelPattern: /no client|client missing/i,
    variable: 'CLIENT',
    condition: 'null',
    result: 'X'
  },
  { 
    labelPattern: /has reimbursement|reimbursement available/i,
    variable: 'mt_remb',
    condition: '!null',
    result: '✓'
  },
  { 
    labelPattern: /no reimbursement|reimbursement unavailable/i,
    variable: 'mt_remb',
    condition: 'null',
    result: 'X'
  }
];

const EXTENDED_AUTO_CONDITION_FIELDS = [
  // Add to existing AUTO_CONDITION_FIELDS
  { 
    labelPattern: /client required|client needed/i,
    variable: 'CLIENT',
    condition: '!null',
    result: '✓'
  },
  { 
    labelPattern: /no client|client missing/i,
    variable: 'CLIENT',
    condition: 'null',
    result: 'X'
  },
  { 
    labelPattern: /has reimbursement|reimbursement available/i,
    variable: 'mt_remb',
    condition: '!null',
    result: '✓'
  },
  { 
    labelPattern: /no reimbursement|reimbursement unavailable/i,
    variable: 'mt_remb',
    condition: 'null',
    result: 'X'
  }
];


const NULL_CHECK_CONDITIONS = [
  { variable: 'CLIENT', condition: 'null', result: 'No Client', description: 'Show text if CLIENT is null/empty' },
  { variable: 'CLIENT', condition: '!null', result: 'Has Client', description: 'Show text if CLIENT is not null/empty' },
  { variable: 'mt_remb', condition: 'null', result: 'No Reimbursement', description: 'Show if reimbursement amount is null' },
  { variable: 'com', condition: '!null', result: 'Has Commission', description: 'Show if commission is not null' }
];

// Default test variables for preview - intentionally empty
const DEFAULT_TEST_VARIABLES = {
  // The requested variables
  VILLE: 'Casablanca',
  CLIENT: 'Exemple Client SARL',
  ADRESSE: '123 Rue, Quartier Central',
  nbre_recu: '3',
  m_pai: 'C/R',
  MNT_TTC: 5000.00,
  ref_exp: '2025001',
  date_exp: '12/03/2025',
  ville_dest: '',
  TEL: '+212 522 123456',
  
  // You can keep some other useful defaults too
  port_p: 'PP',
  nbr_colis: '2'
};

// A4 paper dimensions in pixels (at 96 DPI)
// Landscape orientation: 1123px × 794px
const A4_WIDTH_PX = 1123;
const A4_HEIGHT_PX = 794;

const TemplateDesigner = ({ 
  initialElements = [], 
  onChange,
  readOnly = false,
  onSave
}) => {
  const [elements, setElements] = useState(initialElements);
  const [dragging, setDragging] = useState(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [hoveredElement, setHoveredElement] = useState(null);
  const [jsonData, setJsonData] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConditionModal, setShowConditionModal] = useState(false);
  const [currentElement, setCurrentElement] = useState(null); // For editing
  const [newElementText, setNewElementText] = useState('');
  const [newElementColor, setNewElementColor] = useState('#000000');
  const [newElementWidth, setNewElementWidth] = useState(250);
  const [newElementHeight, setNewElementHeight] = useState(40);
  const [newElementFontSize, setNewElementFontSize] = useState(16); // Added font size state
  const [testVariables, setTestVariables] = useState(DEFAULT_TEST_VARIABLES);
  const [parsedTestVariables, setParsedTestVariables] = useState(DEFAULT_TEST_VARIABLES);
  const [activeTab, setActiveTab] = useState('1');
  const [autoDetectConditions, setAutoDetectConditions] = useState(true);  // New state for auto-detection toggle

  // Conditional logic states
  const [conditionVariable, setConditionVariable] = useState('port_p');
  const [conditionValue, setConditionValue] = useState('PP');
  const [conditionResult, setConditionResult] = useState('X');
  const [conditionType, setConditionType] = useState('equals'); // equals, not-equals, greater-than, less-than
  
  const canvasRef = useRef(null);
  const elementsRef = useRef(elements);
  
  // Keep elementsRef updated with latest elements state
  useEffect(() => {
    elementsRef.current = elements;
  }, [elements]);
  
  // Initialize elements from props - but only when not dragging
  useEffect(() => {
    if (!isDragging && initialElements && initialElements.length > 0) {
      setElements(initialElements);
      updateJsonData(initialElements);
    }
  }, [initialElements, isDragging]);
  
  // When elements change, notify parent component
  useEffect(() => {
    if (onChange && !isDragging) {
      onChange(elements);
    }
  }, [elements, onChange, isDragging]);
  
  // CSS styles for the component - updated with A4 paper dimensions
  const styles = {
    container: {
      width: '100%',
      margin: '0 auto',
      padding: '16px'
    },
    // A4 landscape canvas - using exact A4 dimensions
    canvasContainer: {
      width: `${A4_WIDTH_PX}px`,
      height: `${A4_HEIGHT_PX}px`,
      maxWidth: '100%',
      position: 'relative',
      backgroundColor: 'white',
      overflow: 'hidden',
      border: '4px solid #f0f0f0',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      borderRadius: '2px',
      margin: '0 auto' // Center the canvas
    },
    canvasWrapper: {
      width: '100%',
      overflowX: 'auto',
      marginBottom: '16px'
    },
    element: {
      position: 'absolute',
      cursor: readOnly ? 'default' : 'move',
      padding: '4px',
      border: '1px dashed #d9d9d9',
      borderRadius: '2px'
    },
    elementLabel: {
      fontWeight: 'bold'
    },
    elementControls: {
      position: 'absolute',
      top: '-28px',
      right: '0',
      display: 'flex',
      gap: '4px',
      opacity: 0,
      transition: 'opacity 0.2s ease-in-out'
    },
    elementWrapper: {
      position: 'absolute',
      padding: '0',
      margin: '0'
    },
    elementHovered: {
      border: '1px solid #1890ff',
      backgroundColor: 'rgba(24, 144, 255, 0.04)'
    },
    emptyCanvasMessage: {
      position: 'absolute',
      inset: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#bfbfbf',
      flexDirection: 'column',
      gap: '16px'
    },
    variableTable: {
      maxHeight: '350px',
      overflow: 'auto'
    }
  };
  
  // Update test variables when user changes a value in the table
  const handleTestVariableChange = (key, value) => {
    setTestVariables(prev => ({
      ...prev,
      [key]: value
    }));
    
    setParsedTestVariables(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  // Add a row to test variables
  const addTestVariable = (variableName) => {
    if (!testVariables[variableName]) {
      setTestVariables(prev => ({
        ...prev,
        [variableName]: ""
      }));
      
      setParsedTestVariables(prev => ({
        ...prev,
        [variableName]: ""
      }));
    }
  };
  
  // FIXED: Process template text and replace variables for preview
 // processTemplateText.js


/**
 * Processes template text by replacing variables, handling conditionals,
 * and converting numbers to French words
 * 
 * @param {string} text - Template text with variables and conditionals
 * @param {Object} parsedTestVariables - Object containing variable values
 * @returns {string} - Processed text with replacements
 */
const processTemplateText = (text) => {
  if (!text) return '';
  
  // First handle null and !null conditional logic patterns
  let processedText = text.replace(/\{\{if:([^:]+?)(null|!null):([^}]+)\}\}/g, 
    (match, variable, operator, result) => {
      const trimmedVariable = variable.trim();
      const trimmedOperator = operator.trim();
      const trimmedResult = result.trim();
      
      // Get the actual variable value
      const actualValue = parsedTestVariables[trimmedVariable] !== undefined 
        ? String(parsedTestVariables[trimmedVariable]) 
        : '';
      
      // Determine if condition is satisfied
      let conditionMet = false;
      
      // Handle special null check cases
      if (trimmedOperator === 'null') {
        // Check if the value is null, undefined, empty string, or just whitespace
        conditionMet = !actualValue || actualValue.trim() === '';
      } else if (trimmedOperator === '!null') {
        // Check if the value exists and is not just whitespace
        conditionMet = actualValue && actualValue.trim() !== '';
      }
      
      return conditionMet ? trimmedResult : '';
    });
  
  // Then handle regular conditional logic with operators
  processedText = processedText.replace(/\{\{if:([^:=!<>]+)([=!<>]+)([^:]*):([^}]+)\}\}/g, 
    (match, variable, operator, value, result) => {
      const trimmedVariable = variable.trim();
      const trimmedOperator = operator.trim();
      const trimmedValue = value.trim();
      const trimmedResult = result.trim();
      
      // Get the actual variable value
      const actualValue = parsedTestVariables[trimmedVariable] !== undefined 
        ? String(parsedTestVariables[trimmedVariable]) 
        : '';
      
      // Determine if condition is satisfied
      let conditionMet = false;
      
      // Handle regular comparison operators
      switch (trimmedOperator) {
        case '=':
        case '==':
          conditionMet = actualValue === trimmedValue;
          break;
        case '!=':
          conditionMet = actualValue !== trimmedValue;
          break;
        case '>':
          conditionMet = Number(actualValue) > Number(trimmedValue);
          break;
        case '<':
          conditionMet = Number(actualValue) < Number(trimmedValue);
          break;
        case '>=':
          conditionMet = Number(actualValue) >= Number(trimmedValue);
          break;
        case '<=':
          conditionMet = Number(actualValue) <= Number(trimmedValue);
          break;
        default:
          conditionMet = actualValue === trimmedValue;
      }
      
      return conditionMet ? trimmedResult : '';
    });

  // Handle number-to-French conversion syntax {{num:VARIABLE}}
  processedText = processedText.replace(/\{\{num:([^}]+)\}\}/g, (match, varName) => {
    const trimmedName = varName.trim();
    if (parsedTestVariables[trimmedName] !== undefined) {
      const numValue = parseFloat(parsedTestVariables[trimmedName]);
      if (!isNaN(numValue)) {
        // Convert the number to French words
        return numberToFrenchWords(numValue);
      }
    }
    return ''; // Return empty if variable not found or not a number
  });
  
  // Finally handle regular variable placeholders
  processedText = processedText.replace(/\{\{([^}]+)\}\}/g, (match, varName) => {
    const trimmedName = varName.trim();
    return parsedTestVariables[trimmedName] !== undefined 
      ? parsedTestVariables[trimmedName] 
      : ''; // Replace with empty string if variable not found
  });
  
  return processedText;
};

  // Auto-detect potential condition fields based on element text
  const autoDetectConditionFields = (text) => {
    if (!text || !autoDetectConditions) return text;
    
    let processedText = text;
    
    // Try to match text patterns that might benefit from conditions
    for (const mapping of AUTO_CONDITION_FIELDS) {
      if (mapping.labelPattern.test(text)) {
        // Generate the appropriate condition syntax based on the condition type
        let conditionSyntax;
        if (mapping.condition === 'null' || mapping.condition === '!null') {
          // For null checks, we don't include a value after the operator
          conditionSyntax = `${TEMPLATE_START}if:${mapping.variable}${mapping.condition}:${mapping.result}${TEMPLATE_END}`;
        } else {
          // For regular conditions, use the standard format with equals sign
          conditionSyntax = `${TEMPLATE_START}if:${mapping.variable}=${mapping.condition}:${mapping.result}${TEMPLATE_END}`;
        }
        
        // Only add if not already present
        const conditionPattern = mapping.condition === 'null' || mapping.condition === '!null'
          ? `if:${mapping.variable}${mapping.condition}`
          : `if:${mapping.variable}=`;
          
        if (!text.includes(conditionPattern)) {
          processedText = `${text} ${conditionSyntax}`;
        }
      }
    }
    
    return processedText;
  };



  const conditionTypeDropdown = (
    <Form.Item label="Condition Type">
      <Select
        value={conditionType}
        onChange={setConditionType}
        style={{ width: '100%' }}
      >
        <Option value="equals">equals (=)</Option>
        <Option value="not-equals">not equals (!=)</Option>
        <Option value="greater-than">greater than (&gt;)</Option>
        <Option value="less-than">less than (&lt;)</Option>
        <Option value="greater-or-equal">greater or equal (≥)</Option>
        <Option value="less-or-equal">less or equal (≤)</Option>
        <Option value="is-null">is null/empty</Option>
        <Option value="is-not-null">is not null/empty</Option>
      </Select>
    </Form.Item>
  );
  
  // Generate a condition template string based on inputs
  const generateConditionTemplate = () => {
    let operator = '=';
    let valueToUse = conditionValue;
    
    switch (conditionType) {
      case 'not-equals': operator = '!='; break;
      case 'greater-than': operator = '>'; break;
      case 'less-than': operator = '<'; break;
      case 'greater-or-equal': operator = '>='; break;
      case 'less-or-equal': operator = '<='; break;
      case 'is-null': 
        operator = 'null'; 
        valueToUse = '';
        break;
      case 'is-not-null': 
        operator = '!null'; 
        valueToUse = '';
        break;
      default: operator = '=';
    }
    
    // For null checks, we use just the operator without a value
    if (operator === 'null' || operator === '!null') {
      return `${TEMPLATE_START}if:${conditionVariable}${operator}:${conditionResult}${TEMPLATE_END}`;
    }
    
    return `${TEMPLATE_START}if:${conditionVariable}${operator}${valueToUse}:${conditionResult}${TEMPLATE_END}`;
  };


  // Show condition builder modal
  const showConditionBuilder = () => {
    setShowConditionModal(true);
  };
  
  // Insert condition into the current text element
  const insertCondition = () => {
    const condition = generateConditionTemplate();
    if (showEditModal) {
      setNewElementText(prev => prev + ' ' + condition);
    } else {
      setNewElementText(condition);
    }
    setShowConditionModal(false);
  };
  
  // Apply a predefined condition
const applyPredefinedCondition = (preset) => {
  setConditionVariable(preset.variable);
  setConditionValue(preset.condition === 'null' || preset.condition === '!null' ? '' : preset.condition);
  setConditionResult(preset.result);
  
  // Set the correct condition type based on the condition value
  if (preset.condition === 'null') {
    setConditionType('is-null');
  } else if (preset.condition === '!null') {
    setConditionType('is-not-null');
  } else if (preset.condition.startsWith('>')) {
    setConditionType('greater-than');
  } else if (preset.condition.startsWith('<')) {
    setConditionType('less-than');
  } else if (preset.condition === 'true' || preset.condition === 'false') {
    setConditionType('equals');
  } else {
    setConditionType('equals');
  }
};
  
  // Copy variable placeholder to clipboard
  const copyVariablePlaceholder = (variableName) => {
    const placeholder = `${TEMPLATE_START}${variableName}${TEMPLATE_END}`;
    navigator.clipboard.writeText(placeholder)
      .then(() => {
        Modal.success({
          title: 'Copied!',
          content: `Placeholder "${placeholder}" copied to clipboard.`,
          duration: 2 // 2 seconds
        });
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };
  
  // Show add element modal
  const showAddElementModal = () => {
    if (readOnly) return;
    
    setNewElementText(``);
    setNewElementColor('#000000');
    setNewElementWidth(250);
    setNewElementHeight(40);
    setNewElementFontSize(16); // Default font size
    setShowAddModal(true);
  };
  
  // Add a new element to the canvas
  const addElement = () => {
    // Pre-process the text to auto-detect potential conditions
    const processedText = autoDetectConditionFields(newElementText || `Text with ${TEMPLATE_START}CLIENT${TEMPLATE_END}`);
    
    const newElement = {
      id: `element-${Date.now()}`, // Using timestamp for more unique IDs
      x: 0,
      y: 0,
      width: parseInt(newElementWidth) || 250,
      height: parseInt(newElementHeight) || 40,
      textColor: newElementColor || '#000000',
      fontSize: parseInt(newElementFontSize) || 16, // Added font size
      text: processedText
    };
    const updatedElements = [...elements, newElement];
    setElements(updatedElements);
    updateJsonData(updatedElements);
    setShowAddModal(false);
  };
  
  // Show edit element modal
  const showElementEditModal = (element) => {
    if (readOnly) return;
    
    setCurrentElement(element);
    setNewElementText(element.text);
    setNewElementColor(element.textColor || '#000000');
    setNewElementWidth(element.width);
    setNewElementHeight(element.height);
    setNewElementFontSize(element.fontSize || 16); // Load existing font size or default
    setShowEditModal(true);
  };
  
  // Update an existing element
  const updateElement = () => {
    if (!currentElement) return;
    
    // Pre-process the text to auto-detect potential conditions
    const processedText = autoDetectConditionFields(newElementText);
    
    const updatedElements = elements.map(element => 
      element.id === currentElement.id 
        ? { 
            ...element, 
            text: processedText,
            textColor: newElementColor,
            width: parseInt(newElementWidth) || element.width,
            height: parseInt(newElementHeight) || element.height,
            fontSize: parseInt(newElementFontSize) || 16 // Include font size in update
          } 
        : element
    );
    
    setElements(updatedElements);
    updateJsonData(updatedElements);
    setShowEditModal(false);
    setCurrentElement(null);
  };
  
  // Delete an element
  const deleteElement = (elementId) => {
    if (readOnly) return;
    
    Modal.confirm({
      title: 'Are you sure you want to delete this element?',
      okText: 'Delete',
      okType: 'danger',
      onOk: () => {
        const updatedElements = elements.filter(element => element.id !== elementId);
        setElements(updatedElements);
        updateJsonData(updatedElements);
      }
    });
  };
  
  // Update JSON data display
  const updateJsonData = (data) => {
    setJsonData(JSON.stringify(data, null, 2));
  };
  
  // Handle mouse down on an element
  const handleMouseDown = (e, element) => {
    if (readOnly) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const rect = canvasRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left - element.x;
    const offsetY = e.clientY - rect.top - element.y;
    
    setDragging(element.id);
    setOffset({ x: offsetX, y: offsetY });
    setIsDragging(true);
  };
  
  // Handle mouse move when dragging - memoized to prevent recreation
  const handleMouseMove = useCallback((e) => {
    if (readOnly || !isDragging || !dragging) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const canvasWidth = rect.width;
    const canvasHeight = rect.height;
    
    // Calculate the new position
    let x = e.clientX - rect.left - offset.x;
    let y = e.clientY - rect.top - offset.y;
    
    // Find the current element being dragged
    const currentElement = elementsRef.current.find(el => el.id === dragging);
    if (!currentElement) return;
    
    // Constrain elements to stay within canvas boundaries
    x = Math.max(0, Math.min(x, canvasWidth - currentElement.width));
    y = Math.max(0, Math.min(y, canvasHeight - currentElement.height));
    
    const updatedElements = elementsRef.current.map(element => 
      element.id === dragging 
        ? { ...element, x, y } 
        : element
    );
    
    setElements(updatedElements);
    updateJsonData(updatedElements);
  }, [dragging, isDragging, offset, readOnly]);
  
  // Handle mouse up when dragging - memoized to prevent recreation
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setDragging(null);
      setIsDragging(false);
      
      // Notify parent of changes after drag completes
      if (onChange) {
        onChange(elementsRef.current);
      }
    }
  }, [isDragging, onChange]);
  
  // Clear canvas
  const clearCanvas = () => {
    if (readOnly) return;
    
    Modal.confirm({
      title: 'Clear Canvas',
      content: 'Are you sure you want to clear the canvas? All elements will be deleted.',
      okText: 'Yes, Clear All',
      okType: 'danger',
      onOk: () => {
        setElements([]);
        setJsonData('');
      }
    });
  };

  const printCurrentTemplate = () => {
    // Use the current elements and test variables for printing
    try {
      printTemplate(elements, parsedTestVariables);
    } catch (error) {
      Modal.error({
        title: 'Print Error',
        content: `Failed to print template: ${error.message}`
      });
    }
  };
  
  // Add global event listeners - separate effect without elements dependency
  useEffect(() => {
    if (!isDragging) return;
    
    // Add event listeners to document
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    // Clean up
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Test Variables table columns
  const testVariableColumns = [
    {
      title: 'Variable',
      dataIndex: 'variable',
      key: 'variable',
      width: '30%',
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: '70%',
      render: (text, record) => (
        <Input 
          value={text} 
          onChange={(e) => handleTestVariableChange(record.variable, e.target.value)} 
          disabled={readOnly}
          placeholder="Enter value for testing"
        />
      )
    }
  ];

  // Variable table columns
  const variableColumns = [
    {
      title: 'Variable',
      dataIndex: 'name',
      key: 'name',
      width: '40%',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text code>{text}</Text>
          <Space>
            <Button 
              icon={<CopyOutlined />} 
              size="small" 
              type="text"
              onClick={() => copyVariablePlaceholder(text)}
              title={`Copy ${TEMPLATE_START}${text}${TEMPLATE_END}`}
            />
            <Button
              size="small"
              type="link"
              onClick={() => addTestVariable(text)}
              title="Add to test variables"
            >
              Add
            </Button>
          </Space>
        </div>
      )
    },
   
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: '20%',
      render: (text) => {
        const color = 
          text === 'Number' ? 'blue' : 
          text === 'Date' ? 'green' : 
          text === 'Boolean' ? 'orange' : 'geekblue';
        return <Tag color={color}>{text}</Tag>;
      }
    }
  ];

  // Common conditions table columns
  const conditionColumns = [
    {
      title: 'Condition',
      key: 'description',
      render: (_, record) => (
        <div>
          <div><Text strong>{record.description}</Text></div>
          <div>
            <Text code>
              {record.condition === 'null' || record.condition === '!null'
                ? `if:${record.variable}${record.condition}`
                : `if:${record.variable}=${record.condition}`}
            </Text> → <Text>{record.result}</Text>
          </div>
        </div>
      ),
      width: '70%'
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button 
          type="primary" 
          size="small" 
          onClick={() => applyPredefinedCondition(record)}
        >
          Use
        </Button>
      ),
      width: '30%'
    }
  ];

  // Transform test variables object into table data
  const testVariablesData = Object.entries(testVariables).map(([key, value], index) => ({
    key: index,
    variable: key,
    value: value
  }));
  
  return (
    <div style={styles.container}>
      <Card>
        <Title level={4}>Template Designer</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
          Design your template with variable placeholders and conditional logic
        </Text>
        
        <Alert 
          message="Canvas size matches A4 paper in landscape orientation (1123px × 794px)" 
          type="info" 
          showIcon 
          style={{ marginBottom: '16px' }} 
        />
        
        <Space style={{ marginBottom: '16px' }}>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={showAddElementModal}
            disabled={readOnly}
          >
            Add Text
          </Button>
          
          <Button 
            type="primary"
            icon={<CodeOutlined />}
            onClick={showConditionBuilder}
            disabled={readOnly}
          >
            Add Condition
          </Button>
          
          <Button 
            icon={<ClearOutlined />}
            onClick={clearCanvas}
            disabled={readOnly}
            danger
          >
            Clear Canvas
          </Button>

          <Button 
            type="primary"
            icon={<PrinterOutlined />} 
            onClick={printCurrentTemplate}
            disabled={elements.length === 0}
          >
            Print Preview
          </Button>
          
          <Tooltip title="Automatically detect and add conditions based on text content">
            <Space style={{ marginLeft: '16px' }}>
              <Switch 
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<span />}
                checked={autoDetectConditions}
                onChange={setAutoDetectConditions}
                disabled={readOnly}
              />
              <Text type={autoDetectConditions ? "success" : "secondary"}>
                {autoDetectConditions ? "Auto-conditions ON" : "Auto-conditions OFF"}
              </Text>
            </Space>
          </Tooltip>
        </Space>
        
        {/* Canvas Container - Full width with A4 dimensions */}
        <div style={styles.canvasWrapper}>
          <div 
            ref={canvasRef}
            style={styles.canvasContainer}
          >
            {elements.map(element => (
              <div
                key={element.id}
                style={styles.elementWrapper}
                onMouseEnter={() => !readOnly && setHoveredElement(element.id)}
                onMouseLeave={() => !readOnly && setHoveredElement(null)}
              >
                <div
                  style={{
                    ...styles.element,
                    left: `${element.x}px`,
                    top: `${element.y}px`,
                    width: `${element.width}px`,
                    height: `${element.height}px`,
                    color: element.textColor || '#000',
                    zIndex: dragging === element.id ? 100 : 1,
                    ...(hoveredElement === element.id ? styles.elementHovered : {})
                  }}
                  onMouseDown={(e) => handleMouseDown(e, element)}
                >
                  <div style={{
                    ...styles.elementLabel,
                    fontSize: `${element.fontSize || 16}px` // Apply font size to the element
                  }}>
                    {processTemplateText(element.text || element.id)}
                  </div>
                  
                  {/* Edit/Delete buttons appear on hover */}
                  {!readOnly && (
                    <div 
                      style={{
                        ...styles.elementControls,
                        opacity: hoveredElement === element.id ? 1 : 0
                      }}
                    >
                      <Button 
                        size="small"
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          showElementEditModal(element);
                        }}
                      >
                        Edit
                      </Button>
                      <Button 
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteElement(element.id);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {elements.length === 0 && (
              <div style={styles.emptyCanvasMessage}>
                <InfoCircleOutlined style={{ fontSize: '32px' }} />
                <Text>
                  {readOnly 
                    ? 'This template is empty' 
                    : 'Click "Add Text" to start designing your template'}
                </Text>
              </div>
            )}
          </div>
        </div>
        
        {/* Control Panel - Below the canvas in a row */}
        <div style={{ marginTop: '16px' }}>
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab="Test Variables" key="1">
              <Card size="small" bodyStyle={{ padding: '0' }}>
                <Alert
                  message="For preview only - real variables will be passed at print time"
                  type="info"
                  showIcon
                  style={{ margin: '8px' }}
                />
                <Table 
                  dataSource={testVariablesData} 
                  columns={testVariableColumns}
                  pagination={false}
                  size="small"
                  style={{ maxHeight: '300px', overflowY: 'auto' }}
                />
                <div style={{ padding: '8px', textAlign: 'right' }}>
                  <Button 
                    size="small"
                    onClick={() => setTestVariables(DEFAULT_TEST_VARIABLES)}
                    disabled={readOnly}
                  >
                    Reset to Default
                  </Button>
                </div>
              </Card>
            </TabPane>
            
            <TabPane tab="Available Variables" key="2">
              <div style={styles.variableTable}>
                <Alert
                  message="Click the copy icon to use a variable in your template, or 'Add' to include it in test variables"
                  type="info"
                  showIcon
                  style={{ marginBottom: '8px' }}
                />
                <Table 
                  dataSource={JOURNAL_EXP_VARIABLES}
                  columns={variableColumns}
                  pagination={false}
                  size="small"
                  rowKey="name"
                />
              </div>
            </TabPane>
            
            <TabPane tab="Conditions" key="3">
              <Card size="small" bodyStyle={{ padding: '8px' }}>
                <Alert
                  message="Common conditions for your templates"
                  type="info"
                  showIcon
                  style={{ marginBottom: '8px' }}
                />
                <Table 
                  dataSource={COMMON_CONDITIONS}
                  columns={conditionColumns}
                  pagination={false}
                  size="small"
                  rowKey={(record) => `${record.variable}-${record.condition}`}
                />
                <div style={{ marginTop: '8px' }}>
                  <Button 
                    type="primary" 
                    onClick={showConditionBuilder} 
                    icon={<SettingOutlined />}
                    block
                  >
                    Create Custom Condition
                  </Button>
                </div>
              </Card>
            </TabPane>
            
            <TabPane tab="Template Data" key="4">
              <Card size="small">
                <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
                  This JSON defines your template layout
                </Text>
                <div style={{ 
                  height: '300px', 
                  overflow: 'auto', 
                  backgroundColor: '#f5f5f5', 
                  padding: '8px',
                  borderRadius: '2px',
                  fontFamily: 'monospace',
                  fontSize: '12px'
                }}>
                  <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                    {jsonData || 'No data. Add elements to see template JSON.'}
                  </pre>
                </div>
              </Card>
            </TabPane>
          </Tabs>
        </div>
      </Card>
      
      {/* Add Text Modal */}
      <Modal
        title="Add Text Element"
        open={showAddModal}
        onCancel={() => setShowAddModal(false)}
        onOk={addElement}
        okText="Add"
        width={600}
      >
        <Form layout="vertical">
          <Form.Item 
            label="Text Content" 
            help={`Use ${TEMPLATE_START}variable_name${TEMPLATE_END} to insert variable placeholders or add conditions`}
          >
            <TextArea 
              rows={4}
              value={newElementText}
              onChange={(e) => setNewElementText(e.target.value)}
              placeholder={`Enter text with placeholders like {{CLIENT}} or conditions like {{if:port_p=PP:X}}`}
            />
          </Form.Item>
          
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button 
              type="dashed" 
              onClick={showConditionBuilder}
              icon={<CodeOutlined />}
            >
              Add Condition
            </Button>
            {autoDetectConditions && (
              <Tag color="success" >
                Auto-conditions enabled
              </Tag>
            )}
            <Text type="secondary">
              {autoDetectConditions 
                ? "Conditions will be automatically detected based on text content" 
                : "Add custom logic like 'if port_p=PP, display X'"}
            </Text>
          </div>
          
          <Divider orientation="left">Available Variables</Divider>
          
          <div style={{ marginBottom: '16px', height: '150px', overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: '2px' }}>
            <Table 
              dataSource={JOURNAL_EXP_VARIABLES}
              columns={[
                {
                  title: 'Variable',
                  dataIndex: 'name',
                  key: 'name',
                  width: '30%',
                  render: (text) => (
                    <Button
                      type="link"
                      onClick={() => {
                        setNewElementText(prev => prev + ` ${TEMPLATE_START}${text}${TEMPLATE_END}`);
                      }}
                    >
                      {text}
                    </Button>
                  )
                },
                {
                  title: 'Description',
                  dataIndex: 'description',
                  key: 'description',
                  width: '40%'
                },
                {
                  title: 'Type',
                  dataIndex: 'type',
                  key: 'type',
                  width: '30%',
                  render: (text) => {
                    const color = 
                      text === 'Number' ? 'blue' : 
                      text === 'Date' ? 'green' : 
                      text === 'Boolean' ? 'orange' : 'geekblue';
                    return <Tag color={color}>{text}</Tag>;
                  }
                }
              ]}
              pagination={false}
              size="small"
              rowKey="name"
            />
          </div>
          
          <Divider orientation="left">Element Properties</Divider>
          
          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
            <Form.Item label="Width (px)" style={{ flex: 1, marginBottom: 0 }}>
              <InputNumber 
                min={50}
                max={1000}
                value={newElementWidth}
                onChange={(value) => setNewElementWidth(value)}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label="Height (px)" style={{ flex: 1, marginBottom: 0 }}>
              <InputNumber 
                min={20}
                max={500}
                value={newElementHeight}
                onChange={(value) => setNewElementHeight(value)}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </div>
          
          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item label="Text Color" style={{ flex: 1 }}>
              <Input 
                type="color" 
                value={newElementColor}
                onChange={(e) => setNewElementColor(e.target.value)}
                style={{ width: '100%' }}
              />
            </Form.Item>
            
            {/* Added Font Size control */}
            <Form.Item label="Font Size (px)" style={{ flex: 1 }}>
              <InputNumber
                min={8}
                max={72}
                value={newElementFontSize}
                onChange={(value) => setNewElementFontSize(value)}
                style={{ width: '100%' }}
                addonAfter={<FontSizeOutlined />}
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>
      
      {/* Edit Text Modal */}
      <Modal
        title="Edit Text Element"
        open={showEditModal && currentElement !== null}
        onCancel={() => setShowEditModal(false)}
        onOk={updateElement}
        okText="Update"
        width={600}
      >
        <Form layout="vertical">
          <Form.Item 
            label="Text Content" 
            help={`Use ${TEMPLATE_START}variable_name${TEMPLATE_END} to insert variable placeholders or add conditions`}
          >
            <TextArea 
              rows={4}
              value={newElementText}
              onChange={(e) => setNewElementText(e.target.value)}
              placeholder={`Enter text with placeholders like {{CLIENT}} or conditions like {{if:port_p=PP:X}}`}
            />
          </Form.Item>
          
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button 
              type="dashed" 
              onClick={showConditionBuilder}
              icon={<CodeOutlined />}
            >
              Add Condition
            </Button>
            {autoDetectConditions && (
              <Tag color="success" >
                Auto-conditions enabled
              </Tag>
            )}
            <Text type="secondary">
              {autoDetectConditions 
                ? "Conditions will be automatically detected based on text content" 
                : "Add custom logic like 'if port_p=PP, display X'"}
            </Text>
          </div>
          
          <Divider orientation="left">Available Variables</Divider>
          
          <div style={{ marginBottom: '16px', height: '150px', overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: '2px' }}>
            <Table 
              dataSource={JOURNAL_EXP_VARIABLES}
              columns={[
                {
                  title: 'Variable',
                  dataIndex: 'name',
                  key: 'name',
                  width: '30%',
                  render: (text) => (
                    <Button
                      type="link"
                      onClick={() => {
                        setNewElementText(prev => prev + ` ${TEMPLATE_START}${text}${TEMPLATE_END}`);
                      }}
                    >
                      {text}
                    </Button>
                  )
                },
                {
                  title: 'Description',
                  dataIndex: 'description',
                  key: 'description',
                  width: '40%'
                },
                {
                  title: 'Type',
                  dataIndex: 'type',
                  key: 'type',
                  width: '30%',
                  render: (text) => {
                    const color = 
                      text === 'Number' ? 'blue' : 
                      text === 'Date' ? 'green' : 
                      text === 'Boolean' ? 'orange' : 'geekblue';
                    return <Tag color={color}>{text}</Tag>;
                  }
                }
              ]}
              pagination={false}
              size="small"
              rowKey="name"
            />
          </div>
          
          <Divider orientation="left">Element Properties</Divider>
          
          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
            <Form.Item label="Width (px)" style={{ flex: 1, marginBottom: 0 }}>
              <InputNumber 
                min={50}
                max={1000}
                value={newElementWidth}
                onChange={(value) => setNewElementWidth(value)}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label="Height (px)" style={{ flex: 1, marginBottom: 0 }}>
              <InputNumber 
                min={20}
                max={500}
                value={newElementHeight}
                onChange={(value) => setNewElementHeight(value)}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </div>
          
          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item label="Text Color" style={{ flex: 1 }}>
              <Input 
                type="color" 
                value={newElementColor}
                onChange={(e) => setNewElementColor(e.target.value)}
                style={{ width: '100%' }}
              />
            </Form.Item>
            
            {/* Added Font Size control */}
            <Form.Item label="Font Size (px)" style={{ flex: 1 }}>
              <InputNumber
                min={8}
                max={72}
                value={newElementFontSize}
                onChange={(value) => setNewElementFontSize(value)}
                style={{ width: '100%' }}
                addonAfter={<FontSizeOutlined />}
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>
      
      {/* Condition Builder Modal */}
      <Modal
        title="Condition Builder"
        open={showConditionModal}
        onCancel={() => setShowConditionModal(false)}
        onOk={insertCondition}
        okText="Insert Condition"
        width={600}
      >
        <Alert
          message="Create conditions like 'if port_p=PP, display X'"
          description="Select a variable, condition, and result to insert a condition into your template"
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Variable">
                <Select
                  value={conditionVariable}
                  onChange={setConditionVariable}
                  style={{ width: '100%' }}
                >
                  {JOURNAL_EXP_VARIABLES.map(variable => (
                    <Option key={variable.name} value={variable.name}>
                      {variable.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={6}>
  <Form.Item label="Condition Type">
    <Select
      value={conditionType}
      onChange={setConditionType}
      style={{ width: '100%' }}
    >
      <Option value="equals">equals (=)</Option>
      <Option value="not-equals">not equals (!=)</Option>
      <Option value="greater-than">greater than (&gt;)</Option>
      <Option value="less-than">less than (&lt;)</Option>
      <Option value="greater-or-equal">greater or equal (≥)</Option>
      <Option value="less-or-equal">less or equal (≤)</Option>
      <Option value="is-null">is null/empty</Option>
      <Option value="is-not-null">is not null/empty</Option>
    </Select>
  </Form.Item>
</Col>
            
            <Col span={10}>
  {conditionType !== 'is-null' && conditionType !== 'is-not-null' ? (
    <Form.Item label="Value">
      <Input
        value={conditionValue}
        onChange={(e) => setConditionValue(e.target.value)}
        placeholder="Value to compare with"
      />
    </Form.Item>
  ) : (
    <Form.Item label="Value">
      <Alert
        message="No value needed for null checks"
        type="info"
        showIcon
        style={{ marginTop: '8px' }}
      />
    </Form.Item>
  )}
</Col>
          </Row>
          
          <Form.Item label="Result (what to display if condition is true)">
            <Input
              value={conditionResult}
              onChange={(e) => setConditionResult(e.target.value)}
              placeholder="Text to display when condition is true"
            />
          </Form.Item>
          
          <Form.Item label="Preview">
            <Input 
              value={generateConditionTemplate()}
              readOnly
              addonBefore={<CodeOutlined />}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
          
          <Divider orientation="left">Common Conditions</Divider>
          
          <div style={{ height: '200px', overflow: 'auto' }}>
            <Table 
              dataSource={COMMON_CONDITIONS}
              columns={conditionColumns}
              pagination={false}
              size="small"
              rowKey={(record) => `${record.variable}-${record.condition}`}
            />
          </div>
        </Form>
      </Modal>
    </div>
  );
};

TemplateDesigner.propTypes = {
  initialElements: PropTypes.array,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  onSave: PropTypes.func
};

export default TemplateDesigner;